import { useEffect, useState } from "react";

interface BloodSugarGraphProps {
  type: "low" | "medium" | "high";
  label?: string;
  animate?: boolean;
}

export default function BloodSugarGraph({ type, label, animate = true }: BloodSugarGraphProps) {
  const [progress, setProgress] = useState(animate ? 0 : 1);

  useEffect(() => {
    if (!animate) return;
    const duration = 1500;
    const start = performance.now();

    const animateFrame = (now: number) => {
      const elapsed = now - start;
      const p = Math.min(elapsed / duration, 1);
      setProgress(p);
      if (p < 1) requestAnimationFrame(animateFrame);
    };

    requestAnimationFrame(animateFrame);
  }, [animate]);

  // Different curve paths for each GI level
  const curves = {
    low: {
      path: "M 0 70 Q 30 65, 60 55 Q 90 45, 120 40 Q 180 35, 240 40 Q 300 45, 360 55 Q 390 60, 400 65",
      color: "var(--color-gi-low)",
      title: "Steady Energy",
      description: "Gradual rise, sustained energy, gentle return",
    },
    medium: {
      path: "M 0 70 Q 40 60, 80 40 Q 120 20, 160 25 Q 200 30, 240 45 Q 300 60, 360 65 Q 380 68, 400 70",
      color: "var(--color-gi-medium)",
      title: "Moderate Impact",
      description: "Noticeable rise, then gradual decline",
    },
    high: {
      path: "M 0 70 Q 30 50, 60 20 Q 90 5, 120 10 Q 150 20, 180 45 Q 220 70, 260 80 Q 300 85, 340 80 Q 370 75, 400 70",
      color: "var(--color-gi-high)",
      title: "Spike & Crash",
      description: "Rapid spike, then energy crash",
    },
  };

  const curve = curves[type];

  return (
    <div className="w-full">
      {label && (
        <div className="mb-2">
          <span className="text-xs text-muted font-[family-name:--font-ui] font-medium">{label}</span>
        </div>
      )}
      <div className="pt-3 pb-2">
        <div className="flex items-center gap-2 mb-3">
          <div
            className={`w-2.5 h-2.5 rounded-full ${
              type === "low" ? "bg-gi-low" : type === "medium" ? "bg-gi-medium" : "bg-gi-high"
            }`}
          />
          <span className={`text-sm font-semibold font-[family-name:--font-ui] ${
            type === "low" ? "text-gi-low" : type === "medium" ? "text-gi-medium" : "text-gi-high"
          }`}>
            {curve.title}
          </span>
        </div>

        <svg viewBox="0 0 400 100" className="w-full h-24">
          {/* Background grid */}
          <defs>
            <pattern id={`grid-${type}`} width="40" height="20" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 20" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="400" height="100" fill={`url(#grid-${type})`} />

          {/* Baseline */}
          <line x1="0" y1="70" x2="400" y2="70" stroke="var(--color-muted)" strokeWidth="0.75" strokeDasharray="4" />
          <text x="5" y="78" fontSize="8" fill="var(--color-muted)" fontFamily="var(--font-ui)">baseline</text>

          {/* Animated curve */}
          <path
            d={curve.path}
            fill="none"
            stroke={curve.color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="600"
            strokeDashoffset={600 - progress * 600}
            style={{ transition: animate ? "none" : "stroke-dashoffset 0.3s" }}
          />

          {/* Filled area under curve */}
          <path
            d={`${curve.path} L 400 100 L 0 100 Z`}
            fill={curve.color}
            opacity={0.1 * progress}
          />

          {/* Time labels */}
          <text x="10" y="95" fontSize="8" fill="var(--color-muted)" fontFamily="var(--font-ui)">0h</text>
          <text x="100" y="95" fontSize="8" fill="var(--color-muted)" fontFamily="var(--font-ui)">1h</text>
          <text x="200" y="95" fontSize="8" fill="var(--color-muted)" fontFamily="var(--font-ui)">2h</text>
          <text x="300" y="95" fontSize="8" fill="var(--color-muted)" fontFamily="var(--font-ui)">3h</text>
          <text x="380" y="95" fontSize="8" fill="var(--color-muted)" fontFamily="var(--font-ui)">4h</text>
        </svg>

        <p className={`text-xs font-[family-name:--font-ui] mt-1 ${
          type === "low" ? "text-gi-low" : type === "medium" ? "text-gi-medium" : "text-gi-high"
        }`}>
          {curve.description}
        </p>
      </div>
    </div>
  );
}

// Comparison view showing all three curves
export function BloodSugarComparison() {
  return (
    <div className="space-y-4">
      <div className="section-label">Blood Sugar Response by GI Level</div>
      <div className="grid gap-4 md:grid-cols-3">
        <BloodSugarGraph type="low" label="Low GI (0-55)" animate={false} />
        <BloodSugarGraph type="medium" label="Medium GI (56-69)" animate={false} />
        <BloodSugarGraph type="high" label="High GI (70+)" animate={false} />
      </div>
    </div>
  );
}
